import React from 'react';

import { Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGoalsCascadedViewData } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import RabbitGoalProgressBar from 'screens/Team/Rabbit/Goals/Audit/RabbitGoalProgressBar';

import { Goal, OpenSidePanel } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/newIllustrations/noGoal.svg';

import { TASK_TYPES_2 } from 'constants/ProjectConstants';
import { sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { showTaskDetails } from 'services/taskDetails';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { ProfileSummaryCardTemplate } from '../ProfileSummaryCardTemplate';
import { GoalsMySummaryAgGrid } from './components/GoalsMySummaryAgGrid';
import { useTimeBoundTimePeriods } from './hooks';

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
`;

const StyledAvatarGroup = styled(AvatarGroup)`
  display: flex;
  ${Container}:hover & {
    visibility: visible;
    display: none;
  }
`;
const ViewDetailsButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;
const ButtonContainer = styled('div')`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const GoalsMySummaryRow = ({
  id,
  goalName,
  goalProgressLevel,
  completionPercentage = 0,
  dueDate,
  employees = [],
  showDivider,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const showDetails = ({ index, taskId, type, originalGoal }) => {
    sendOpenedKRPanelEvent({
      eventProperties: {
        source: 'Home Page',
      },
    });
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: null,
        employee: {},
        type: TASK_TYPES_2.goal,
        history,
      })
    );
  };

  const DateText = ({ children }) => (
    <TextBodyMedium mt="2px" color="text.g2">
      {children}
    </TextBodyMedium>
  );

  const employeesCollaboratorsFirst = employees?.sort((a, b) => {
    if (a.ownerType < b.ownerType) {
      return 1;
    }
    if (a.ownerType > b.ownerType) {
      return -1;
    }
    return 0;
  });

  return (
    <>
      <Container
        onClick={() => {
          showDetails({ taskId: id });
        }}
      >
        <Flex width="100%">
          <Flex flexDir="column" width="100%">
            <TextBodyMedium className="highlightTextOnHover">{goalName}</TextBodyMedium>
            <Flex mt="20px" alignItems="center">
              <Flex flexDir="column" w="242px">
                <RabbitGoalProgressBar
                  goalProgressLevel={goalProgressLevel}
                  percentageValue={completionPercentage?.toFixed(0)}
                />
                {dueDate && (
                  <DateComp
                    date={dueDate}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor
                    textComponent={DateText}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                )}
              </Flex>
              <StyledAvatarGroup ml="auto">
                {employeesCollaboratorsFirst?.map(({ displayName, profilePhoto, id, ownerType }) => (
                  <Avatar
                    sx={{ border: ownerType === 'OWNER' ? '1px solid #60C861 !important' : '0' }}
                    name={displayName}
                    src={profilePhoto}
                    key={id}
                  />
                ))}
              </StyledAvatarGroup>
              <ButtonContainer className="showOnHover">
                <ViewDetailsButton
                  onClick={() => {
                    showDetails({ taskId: id });
                  }}
                  variant="rowButtonOutlined"
                  StartIcon={OpenSidePanel}
                >
                  View Details
                </ViewDetailsButton>
              </ButtonContainer>
            </Flex>
          </Flex>
        </Flex>
      </Container>
      {showDivider && <Divider color="neutral.CFD7DF" />}
    </>
  );
};

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const GoalsProfileSummaryCard = ({ employeeId }) => {
  const history = useHistory();

  const { data: timelineData, isLoading: timelineAPILoading } = useTimeBoundTimePeriods();

  // const { data, isLoading, isError } = useMySummaryGoalsV1({
  //   limit: 3,
  //   offset: 0,
  //   enabled: Boolean(timelineData?.current),
  //   timeBounds: timelineData?.current ? [timelineData?.current] : null
  // });

  const sideNavSelectedCohort = null;

  const { data, isLoading } = useGoalsCascadedViewData({
    attributeFilter: {
      filterConditions: [
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [employeeId],
        },
        {
          type: 'TYPE',
          operatorType: 'IN',
          data: ['GOAL'],
        },
      ],
    },
    cohort: sideNavSelectedCohort,
    groupByKeys: GROUP_BY,
    splitByKeys: SPLIT_BY,
    orderBy: null,
    timeBounds: timelineData?.current ? [timelineData?.current] : [null],
    isHierarchyView: false,
    pageType: 'PROFILE_PAGE',
  });

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries([
      REACT_QUERY_KEYS.GoalsCascadedData,
      sideNavSelectedCohort?.id,
      GROUP_BY,
      SPLIT_BY,
      null,
      [timelineData?.current],
      {
        filterConditions: [
          {
            type: 'NAME',
            operatorType: 'IN',
            data: [employeeId],
          },
          {
            type: 'TYPE',
            operatorType: 'IN',
            data: ['GOAL'],
          },
        ],
      },
      false,
    ]);
  };

  if (isLoading || timelineAPILoading) {
    return (
      <ProfileSummaryCardTemplate showViewAllButton={false} Icon={Goal} heading="Goals">
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </ProfileSummaryCardTemplate>
    );
  }
  if (!data?.length) {
    return (
      <EmptySummaryCard
        Icon={Goal}
        heading="Goals"
        description="They haven't created any goals yet"
        illustrationSrc={NoDataImg}
      />
    );
  }
  return (
    <ProfileSummaryCardTemplate Icon={Goal} heading="Goals" showViewAllButton={false} onClickViewAll={() => {}}>
      <GoalsMySummaryAgGrid
        refetchCascadedViewData={refetchCascadedViewData}
        data={data}
        selectedTimeline={timelineData?.current}
        isProfilePage
      />
    </ProfileSummaryCardTemplate>
  );
};

export { GoalsProfileSummaryCard };
